import jsPDF from "jspdf";
import notoSansDevanagariUrl from "@/assets/fonts/NotoSansDevanagari-Variable.ttf?url";

const FONT_FAMILY = "Noto Sans Devanagari";

/**
 * jsPDF's built-in fonts are WinAnsi only. Devanagari written with those
 * fonts comes out as unrelated symbols. The browser shapes Hindi correctly,
 * so scripts that contain it are drawn onto the page and embedded as images.
 */
const DEVANAGARI =
  /[\u0900-\u097F\u1CD0-\u1CFF\uA8E0-\uA8FF]/;

const PX_PER_MM = 10;
const PAGE_WIDTH_MM = 210;
const PAGE_HEIGHT_MM = 297;
const MARGIN_MM = 18;

const pageWidthPx = PAGE_WIDTH_MM * PX_PER_MM;
const pageHeightPx = PAGE_HEIGHT_MM * PX_PER_MM;
const marginPx = MARGIN_MM * PX_PER_MM;
const contentWidthPx = pageWidthPx - marginPx * 2;

const ptToPx = (pt: number) => pt * (25.4 / 72) * PX_PER_MM;

const TITLE_PX = ptToPx(22);
const META_PX = ptToPx(12);
const BODY_PX = ptToPx(12);
const FOOTER_PX = ptToPx(10);
const LINE_HEIGHT = BODY_PX * 1.9;
const BLANK_LINE_HEIGHT = BODY_PX * 0.85;

let devanagariFontPromise: Promise<void> | null = null;

const loadDevanagariFace = async () => {
  const source = `url(${notoSansDevanagariUrl})`;
  try {
    const variableFace = new FontFace(FONT_FAMILY, source, {
      weight: "100 900",
      stretch: "62.5% 100%",
      style: "normal",
    });
    return await variableFace.load();
  } catch {
    const regularFace = new FontFace(FONT_FAMILY, source, { style: "normal" });
    return regularFace.load();
  }
};

const ensureDevanagariFont = () => {
  if (!devanagariFontPromise) {
    devanagariFontPromise = (async () => {
      document.fonts.add(await loadDevanagariFace());
      await document.fonts.load(`400 ${BODY_PX}px "${FONT_FAMILY}"`);
      await document.fonts.load(`700 ${TITLE_PX}px "${FONT_FAMILY}"`);
    })().catch((error) => {
      devanagariFontPromise = null;
      throw error;
    });
  }
  return devanagariFontPromise;
};

type GraphemeSegmenter = {
  segment: (input: string) => Iterable<{ segment: string }>;
};

const graphemeSegmenter: GraphemeSegmenter | null = (() => {
  const segmenterCtor = (
    Intl as typeof Intl & {
      Segmenter?: new (
        locales: undefined,
        options: { granularity: "grapheme" },
      ) => GraphemeSegmenter;
    }
  ).Segmenter;
  if (!segmenterCtor) return null;
  return new segmenterCtor(undefined, { granularity: "grapheme" });
})();

const graphemes = (value: string) => {
  if (!graphemeSegmenter) return Array.from(value);
  return Array.from(graphemeSegmenter.segment(value), (part) => part.segment);
};

const wrapLine = (
  ctx: CanvasRenderingContext2D,
  line: string,
  maxWidth: number,
) => {
  const words = line.split(" ").filter((word) => word.length > 0);
  if (words.length === 0) return [""];

  const lines: string[] = [];
  let current = "";

  const appendFitted = (word: string) => {
    let chunk = "";
    for (const grapheme of graphemes(word)) {
      const next = chunk + grapheme;
      if (chunk && ctx.measureText(next).width > maxWidth) {
        lines.push(chunk);
        chunk = grapheme;
      } else {
        chunk = next;
      }
    }
    current = chunk;
  };

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth) {
      current = candidate;
      continue;
    }
    if (current) lines.push(current);
    if (ctx.measureText(word).width > maxWidth) {
      appendFitted(word);
    } else {
      current = word;
    }
  }

  if (current) lines.push(current);
  return lines;
};

const prepareLines = (ctx: CanvasRenderingContext2D, text: string) => {
  const normalized = text
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\t/g, "  ")
    .normalize("NFC");

  ctx.font = `400 ${BODY_PX}px "${FONT_FAMILY}"`;

  const lines: string[] = [];
  for (const paragraph of normalized.split("\n")) {
    lines.push(...wrapLine(ctx, paragraph, contentWidthPx));
  }
  return lines;
};

type PagePlan = {
  lines: string[];
  showHeader: boolean;
  showFooter: boolean;
};

const headerBlockHeight = () => TITLE_PX + META_PX + ptToPx(16);

const lineAdvance = (line: string) =>
  line === "" ? BLANK_LINE_HEIGHT : LINE_HEIGHT;

const paginate = (lines: string[]): PagePlan[] => {
  const pages: PagePlan[] = [];
  const bottom = pageHeightPx - marginPx;
  let index = 0;
  let showHeader = true;

  while (index < lines.length) {
    let y = marginPx + (showHeader ? headerBlockHeight() : 0);
    const bucket: string[] = [];

    while (index < lines.length) {
      const advance = lineAdvance(lines[index]);
      if (bucket.length > 0 && y + advance > bottom) break;
      bucket.push(lines[index]);
      y += advance;
      index += 1;
    }

    if (bucket.length === 0) break;
    pages.push({ lines: bucket, showHeader, showFooter: false });
    showHeader = false;
  }

  if (pages.length === 0) {
    pages.push({ lines: [], showHeader: true, showFooter: true });
    return pages;
  }

  const last = pages[pages.length - 1];
  let used =
    marginPx + (last.showHeader ? headerBlockHeight() : 0);
  for (const line of last.lines) used += lineAdvance(line);

  const footerBlock = ptToPx(8) + FOOTER_PX;
  if (used + footerBlock <= pageHeightPx - marginPx) {
    last.showFooter = true;
  } else {
    pages.push({ lines: [], showHeader: false, showFooter: true });
  }

  return pages;
};

const drawPage = (
  page: PagePlan,
  title: string,
  dateLabel: string,
) => {
  const canvas = document.createElement("canvas");
  canvas.width = pageWidthPx;
  canvas.height = pageHeightPx;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not create a canvas for the script PDF.");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#111111";
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  ctx.direction = "ltr";

  let y = marginPx;

  if (page.showHeader) {
    ctx.font = `700 ${TITLE_PX}px "${FONT_FAMILY}"`;
    ctx.fillText(title, marginPx, y);
    y += TITLE_PX + ptToPx(2);

    ctx.strokeStyle = "#111111";
    ctx.lineWidth = Math.max(1, PX_PER_MM * 0.35);
    ctx.beginPath();
    ctx.moveTo(marginPx, y);
    ctx.lineTo(pageWidthPx - marginPx, y);
    ctx.stroke();

    y += ptToPx(5);
    ctx.fillStyle = "#111111";
    ctx.font = `400 ${META_PX}px "${FONT_FAMILY}"`;
    ctx.fillText(dateLabel, marginPx, y);
    y += META_PX + ptToPx(9);
  }

  ctx.font = `400 ${BODY_PX}px "${FONT_FAMILY}"`;
  ctx.fillStyle = "#111111";
  for (const line of page.lines) {
    if (line) ctx.fillText(line, marginPx, y);
    y += lineAdvance(line);
  }

  if (page.showFooter) {
    y += ptToPx(6);
    ctx.font = `400 ${FOOTER_PX}px "${FONT_FAMILY}"`;
    ctx.fillStyle = "#4b5563";
    ctx.fillText("Generated by PassionBits", marginPx, y);
  }

  return canvas;
};

const downloadUnicodeScriptPdf = async (title: string, text: string) => {
  await ensureDevanagariFont();

  const measureCanvas = document.createElement("canvas");
  const measureCtx = measureCanvas.getContext("2d");
  if (!measureCtx) {
    throw new Error("Could not measure script text for the PDF.");
  }

  const lines = prepareLines(measureCtx, text);
  const pages = paginate(lines);
  const dateLabel = `Date: ${new Date().toLocaleDateString()}`;

  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  doc.setProperties({
    title,
    subject: "Video Script",
    author: "Generated by PassionBits",
    keywords: "video script",
    creator: "PassionBits App",
  });

  pages.forEach((page, index) => {
    if (index > 0) doc.addPage();
    const canvas = drawPage(page, title, dateLabel);
    const image = canvas.toDataURL("image/png");
    doc.addImage(image, "PNG", 0, 0, PAGE_WIDTH_MM, PAGE_HEIGHT_MM);
  });

  doc.save(`${title}.pdf`);
};

const downloadLatinScriptPdf = (title: string, text: string) => {
  const doc = new jsPDF();

  const PAGE_HEIGHT = doc.internal.pageSize.height;
  const PAGE_WIDTH = doc.internal.pageSize.width;

  const TOP_MARGIN = 20;
  const LEFT_MARGIN = 20;
  const RIGHT_MARGIN = 20;
  const BOTTOM_MARGIN = 20;
  const FOOTER_HEIGHT = 10;

  doc.setProperties({
    title,
    subject: "Video Script",
    author: "Generated by PassionBits",
    keywords: "video script",
    creator: "PassionBits App",
  });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.text(title, LEFT_MARGIN, TOP_MARGIN);

  doc.setLineWidth(0.5);
  doc.line(
    LEFT_MARGIN,
    TOP_MARGIN + 5,
    PAGE_WIDTH - RIGHT_MARGIN,
    TOP_MARGIN + 5,
  );

  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.text(
    `Date: ${new Date().toLocaleDateString()}`,
    LEFT_MARGIN,
    TOP_MARGIN + 15,
  );

  const startY = TOP_MARGIN + 30;
  const maxWidth = PAGE_WIDTH - LEFT_MARGIN - RIGHT_MARGIN;
  const wrapped = doc.splitTextToSize(text, maxWidth);

  let y = startY;
  const lineHeight = doc.getFontSize() * 1.2;
  const usableHeight = PAGE_HEIGHT - BOTTOM_MARGIN - FOOTER_HEIGHT;

  wrapped.forEach((line: string) => {
    if (y + lineHeight > usableHeight) {
      doc.addPage();
      y = TOP_MARGIN;
    }

    doc.text(line, LEFT_MARGIN, y);
    y += lineHeight;
  });

  let footerY = y + 10;

  if (footerY + FOOTER_HEIGHT > PAGE_HEIGHT) {
    doc.addPage();
    footerY = PAGE_HEIGHT - BOTTOM_MARGIN;
  }

  doc.setFontSize(10);
  doc.text("Generated by PassionBits", LEFT_MARGIN, footerY);

  doc.save(`${title}.pdf`);
};

export const downloadScriptAsPdf = async (title: string, text: string) => {
  const body = text ?? "";
  if (DEVANAGARI.test(body)) {
    await downloadUnicodeScriptPdf(title, body);
    return;
  }
  downloadLatinScriptPdf(title, body);
};
